import css from "../../css/listing/slider.module.css";

import React, { useRef, useState } from "react";
import Router from "next/router";
import PropTypes from "prop-types";
import ImageGallery from "react-image-gallery";
import SliderArchive from "./SliderArchive";
import { getListingPhotoUrl, LISTING_THUMB_900_529 } from "../../service/photo";
import Logo from "../header/Logo";
import isRent from "../../helper/isRent";
import moneyFormatter from "../../helper/moneyFormatter";
import SliderThumbs from "./SliderThumbs";
import MyPropertyModal from "../my-property/MyPropertyModal";
import { geocodeAddress } from "../../service/my-property";
import { useAuth } from "../common/Auth";
import { getProfileUrlWithRedirectProperty } from "../../service/user";

import EnlargeIcon from "../../static/icons/enlarge.svg";
import CloseIcon from "../../static/icons/close.svg";
import ThumbnailIcon from "../../static/icons/thumbnail.svg";

const Slider = (props) => {
    const { profile } = useAuth();
    const ref = useRef(null);

    const [slideIndex, setSlideIndex] = useState(0);
    const [isModalMyProperty, setIsModalMyProperty] = useState(false);
    const [geocodeData, setGeocodeData] = useState(null);

    const { listing } = props;
    let { primary_photo, photos, status } = listing;

    const isActive = status === "active";

    if (!photos || photos.length === 0) {
        photos = [primary_photo];
    }

    const maxSlideIndex = photos.length - 1;

    const changeSlide = (slide) => {
        if (slide > maxSlideIndex) {
            slide = 0;
        } else if (slide < 0) {
            slide = maxSlideIndex;
        }

        setSlideIndex(slide);
    };

    const renderLeftNav = (onClick, disabled) => {
        return (
            <button
                className="image-gallery-icon btn-prev"
                disabled={disabled}
                onClick={() => changeSlide(slideIndex - 1)}
                role="button"
                aria-label="Previous"
            />
        );
    };

    const renderRightNav = (onClick, disabled) => {
        return (
            <button
                className="image-gallery-icon btn-next"
                disabled={disabled}
                onClick={() => changeSlide(slideIndex + 1)}
                role="button"
                aria-label="Next"
            />
        );
    };

    const renderFullscreenButton = (onClick, isFullscreen) => {
        return isFullscreen ? (
            <button
                type="button"
                className="image-gallery-icon image-gallery-fullscreen-button active"
                onClick={onClick}
            >
                <CloseIcon className="image-gallery-close-svg" />
            </button>
        ) : (
            <button
                type="button"
                className="image-gallery-enlarge-button"
                onClick={onClick}
            >
                Enlarge <EnlargeIcon className="image-gallery-svg" />
            </button>
        );
    };

    const renderCustomControls = () => {
        let currentIndex = 0;
        let indexTotal = photos.length;

        if (ref.current) {
            currentIndex = ref.current.getCurrentIndex() + 1;
        }

        const rent = isRent(listing);

        const priceLabel = rent ? "For Rent " : "For Sale ";

        const price = rent ? (
            <span className="image-gallery-details-price-value">
                {moneyFormatter(0).format(listing.price)}
                <span className="image-gallery-details-price-mo">/mo</span>
            </span>
        ) : (
            <span className="image-gallery-details-price-value">
                {moneyFormatter(0).format(listing.price)}
            </span>
        );

        return (
            <>
                <div className="image-gallery-index-standard">
                    <span className="image-gallery-index-standard-icon">
                        <ThumbnailIcon />
                    </span>
                    <span>{currentIndex}</span>
                    <span> / </span>
                    <span>{indexTotal} Photos</span>
                </div>
                <div className="image-gallery-index">
                    <span className="image-gallery-index-current">
                        {currentIndex}
                    </span>
                    <span className="image-gallery-index-separator"> / </span>
                    <span className="image-gallery-index-total">
                        {indexTotal}
                    </span>
                </div>
                <div className="image-gallery-details">
                    <div className="image-gallery-details-address">
                        {listing.address} {listing.unit_number || ""}
                    </div>
                    <div className="image-gallery-details-price">
                        {priceLabel} {price}
                    </div>
                    <div className="image-gallery-details-logo">
                        <Logo size="sm" color="white" />
                    </div>
                </div>
            </>
        );
    };

    const handleClaimToUnlock = async () => {
        if (profile && (!profile.full_name || !profile.phone)) {
            // redirect to Account Settings / Profile page
            const profileUrl = getProfileUrlWithRedirectProperty(
                `/l/${listing.url}`
            );

            await Router.push(profileUrl.url, profileUrl.as);
            return;
        }

        // ONLY FOR ARCHIVED
        try {
            const geocodeData = await geocodeAddress(listing.full_address);

            setIsModalMyProperty(true);
            setGeocodeData(geocodeData.data);
        } catch (e) {
            await Router.push(
                {
                    pathname: `/myproperty/claim_property`,
                    query: {
                        address: listing.full_address,
                    },
                },
                `/myproperty/claim_property?address=${listing.full_address}`
            );

            return null;
        }
    };

    const handleCloseModal = () => {
        setIsModalMyProperty(false);
        setGeocodeData(null);
    };

    // All ACTIVE LISTING
    // and
    // ARCHIVED USER LISTING - if the user who owns property or is Claimed property goes to it -> it will NOT show claim property stuff
    const forceActive =
        listing.source_user_id &&
        profile &&
        profile.id === listing.source_user_id;

    // IMAGES
    if (!isActive && !forceActive) {
        photos = [photos[0]];
    }

    const description =
        listing.full_address +
        " - " +
        listing.bedrooms +
        " Beds, " +
        listing.bathrooms +
        " Bath";
    const images = photos.map((photo) => {
        return {
            original: getListingPhotoUrl(photo, LISTING_THUMB_900_529),
            originalClass: isActive || forceActive ? null : "archive-image",
            originalAlt: description,
            originalTitle: description,
            description: description,
        };
    });

    return (
        <>
            <div className={css.slider}>
                {!isActive && !forceActive && (
                    <SliderArchive onClick={handleClaimToUnlock} />
                )}

                <ImageGallery
                    ref={ref}
                    items={images}
                    lazyLoad={true}
                    showThumbnails={false}
                    renderLeftNav={renderLeftNav}
                    renderRightNav={renderRightNav}
                    renderFullscreenButton={renderFullscreenButton}
                    renderCustomControls={renderCustomControls}
                    startIndex={slideIndex}
                    showNav={isActive || forceActive}
                    showFullscreenButton={isActive || forceActive}
                    useBrowserFullscreen={false}
                    showPlayButton={false}
                    showIndex={false}
                />

                {photos && (
                    <SliderThumbs
                        listing={listing}
                        onChange={(index) => changeSlide(index)}
                        activeIndex={slideIndex}
                        onArchiveClick={handleClaimToUnlock}
                        forceActive={!!forceActive}
                    />
                )}

                {!isActive &&
                    !forceActive &&
                    isModalMyProperty &&
                    geocodeData && (
                        <MyPropertyModal
                            address={listing.full_address}
                            geocodeData={geocodeData}
                            onClose={handleCloseModal}
                        />
                    )}
            </div>
        </>
    );
};

Slider.propTypes = {
    listing: PropTypes.object.isRequired,
};

export default Slider;
